import '../../product-card/product-card-display';
import './search-filters/create-search-country-filter';
import './search-filters/create-search-state-school-filter';
import debounce from 'lodash/debounce';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import {
  COUNTRY_FACET,
  CreateCountry,
  DEFAULT_COUNTRY_CODE,
  FilterAction,
  SCHOOL_FACET,
  STATE_FACET,
} from './interfaces/create-search-filter.interface';
import { CreateSearchAnalytics } from './create-search.analytics';
import { Modal } from '../../modal/modal';
import { SearchFacet, SearchFacetValue } from '../../search/interfaces/search.interface';
import { StoreState } from '../../../store/store.interface';
import { announceFilterChange, getCountriesAsFacet, updateFacet } from './utils/create-search-filters.util';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import {
  createSearchParam,
  selectCreateFacets,
  selectCreateSearchTotal,
  selectedCreateFacetCount,
} from './store/create-search.selector';
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing, TemplateResult } from 'lit';
import { findElementInContexts } from '../../search/utils/search.util';
import { performCreateSearch } from './thunks/create-search.thunks';
import { repeat } from 'lit/directives/repeat.js';
import { getUserDataForUserAnalytics } from '../../user-profile/user-profile.selectors';
import { selectSearchPageType, selectSelectedCatalog } from '../../search/store/search.selector';
import emit from '../../../common/events';

@customElement('mhecomm-create-search-filters')
export class CreateSearchFilters extends connect(store)(BaseComponent) {
  /**
   * Expand or collapse filters by default.
   */
  @property({ converter: booleanConverter })
  collapseFilter = false;

  /**
   * List of countries to be used as a facet in filters.
   * The facet values are fixed to the list of countries provided.
   */
  @property({ type: Array })
  countries?: CreateCountry[];

  /**
   * Number of facet values to be shown before collapsing and showing "View All" option.
   * For checkbox type facet only.
   */
  @property({ type: Number })
  displayFacetValueLimit = 10;

  /**
   * Show or hide facet header which contains the title "Filter By".
   */
  @property({ converter: booleanConverter })
  showFacetHeader = true;

  /**
   * The current store.
   */
  @property()
  store?: string;

  /**
   * Search facets to be shown as filters.
   */
  @state()
  private facets?: SearchFacet[];

  @state()
  private filterModal;

  private appliedFilter: SearchFacet[] = [];
  private countriesAsFacet?: SearchFacet = undefined;
  private isDefaultCollapsedFilter = true;
  private isFilterRenderedInModal = false;
  private selectedFacetCount = 0;
  private totalSearchResult = 0;
  private selectedCatalog;
  private readonly onFilterSelectionHandler = (event: Event) => this.onFilterSelection(event);
  private readonly handleResize = debounce(() => this.udpateFilterPlacement(), 300);

  /**
   * Initializes the country facet and then calls the parent connectedCallback to set up store connection.
   */
  connectedCallback(): void {
    this.preSelectCountry();
    document.addEventListener('mhecomm-create-search-filter-selection', this.onFilterSelectionHandler as EventListener);
    super.connectedCallback();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('mhecomm-create-search-filter-selection', this.onFilterSelectionHandler as EventListener);
    window.removeEventListener('resize', this.handleResize);
  }

  updated() {
    this.udpateFilterPlacement();
  }

  stateChanged(state: StoreState): void {
    this.facets = selectCreateFacets(state, this.countriesAsFacet);
    this.selectedFacetCount = selectedCreateFacetCount(state);
    this.totalSearchResult = selectCreateSearchTotal(state) || 0;

    this.appliedFilter =
      this.facets?.reduce((arr: any, facet) => {
        const selectedFacetValues = facet?.facetValues.filter((facetValue) => facetValue.selected);
        if (selectedFacetValues.length) {
          arr.push({
            ...facet,
            facetValues: selectedFacetValues,
          });
        }
        return arr;
      }, []) || [];

    this.isDefaultCollapsedFilter = !(this.facets?.length && this.facets?.length <= 2);
    this.selectedCatalog = selectSelectedCatalog(state);
    this.syncFilterModalBody();
  }

  renderComp(): TemplateResult {
    return html`
      <div tabindex="-1" role="region" aria-label="Filters">
        <span id="filter-announcements" aria-live="polite" class="visually-hidden"></span>
        ${this.isFilterRenderedInModal && this.facets?.length
          ? this.renderFilterModal()
          : html`
              ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing}
              ${this.facets?.length ? this.renderFacetHeader() : nothing} ${this.renderFacets()}
            `}
      </div>
    `;
  }

  private renderFacetHeader(): TemplateResult | typeof nothing {
    if (!this.showFacetHeader) {
      return nothing;
    }

    return html`
      <div class="d-flex d-md-block d-lg-flex justify-content-between align-items-end mb-2">
        <div class="d-flex justify-content-between w-100">
          ${this.totalSearchResult > 0 ? html`<h2 class="h6 mb-0 me-2 fw-bold lh-base">Filter By</h2>` : nothing}
        </div>
      </div>
    `;
  }

  /**
   * Renders the facets.
   * The state facet is filtered out since it's being handled together with the school facet.
   */
  private renderFacets(isModal = false): TemplateResult {
    return html`
      ${this.totalSearchResult > 0
        ? this.facets
            ?.filter((facet) => facet.name !== STATE_FACET)
            .map(
              (facet, index): TemplateResult =>
                this.renderFacet(
                  facet,
                  index,
                  facet.name === SCHOOL_FACET
                    ? this.renderStateAndSchoolFacet.bind(this)
                    : facet.name === COUNTRY_FACET
                      ? this.renderCountryFacet.bind(this)
                      : this.renderDefaultFacet.bind(this),
                  isModal
                )
            )
        : nothing}
    `;
  }

  /**
   * Renders a facet with collapse functionality.
   * The facet content is rendered by the provided callback function.
   *
   * @param facet
   * @param index
   * @param callback
   * @param isModal
   */
  private renderFacet(
    facet: SearchFacet,
    index: number,
    callback: (facet: SearchFacet) => TemplateResult | typeof nothing,
    isModal = false,
  ): TemplateResult {
    return html`
      <div class="accordion mb-2" id="${facet.name}-accordion" data-automation-id="${facet.name}-accordion">
        <div class="accordion-item">
          <div>
            <h3 class="accordion-header" id="${facet.name}">
              <button
                class="accordion-button fw-semibold mb-0 shadow-none text-capitalize ${this.checkIfDefaultCollapsedFilter() && !isModal ? 'collapsed' : ''}"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#${facet.name}-collapse-${index}"
                aria-expanded="${!(this.checkIfDefaultCollapsedFilter() && !isModal)}"
                aria-controls="${facet.name}-collapse-${index}"
                @click=${(e) => this.handleCollapseToggle(e, facet.name)}>
                ${facet.label}
              </button>
            </h3>
            <div
              id="${facet.name}-collapse-${index}"
              class="accordion-collapse collapse p-3 ${this.checkIfDefaultCollapsedFilter() && !isModal ? '' : 'show'}"
              aria-labelledby="${facet.name}"
              data-bs-parent="#${facet.name}-accordion">
              ${callback(facet)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderFilterModal(): TemplateResult {
    const modalContextId = 'create-search-filter';
    this.filterModal = new Modal({
      modalContext: {
        componentRef: modalContextId,
        componentRefData: this.analyticDetail(),
      },
      header: 'Filter By',
      body: {
        template: html`
          ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing} ${this.renderFacets(true)}
        `,
      },
      disableFooter: true,
    });

    this.filterModal.addEventListener(`${modalContextId}-modal-close`, () => {
      (this.shadowRoot?.querySelector('.filter-by-button') as HTMLInputElement)?.focus();
    });

    return html`
      <div class="text-center">
        <button
          type="button"
          class="btn btn-outline-primary w-100 filter-by-button"
          data-bs-toggle="modal"
          data-bs-target="#filterModal"
          aria-label="Open filters. ${this.selectedFacetCount
            ? `${this.selectedFacetCount} filters currently selected.`
            : ''}"
          @click=${this.openModal}>
          Filter By ${this.selectedFacetCount ? `(${this.selectedFacetCount})` : ''}
        </button>
      </div>
    `;
  }

  private syncFilterModalBody(): void {
    if (!this.isFilterRenderedInModal) {
      return;
    }

    const activeFilterModal = this.getActiveFilterModal();
    const modal = activeFilterModal || this.filterModal;

    if (!modal) {
      return;
    }

    modal.setBody({
      template: html`
        ${this.appliedFilter.length ? this.renderAppliedFilter() : nothing}
        ${this.renderFacets(true)}
      `,
    });
  }

  private getActiveFilterModal(): Modal | null {
    const modals = Array.from(document.getElementsByTagName('mhecomm-modal')) as Modal[];
    return modals.find((modal) => modal.shadowRoot?.querySelector('#create-search-filter-modal-body')) || null;
  }

  private renderCountryFacet(facet: SearchFacet): TemplateResult {
    // shallow cloning facets value
    const countryFacet = {
      ...facet,
      facetValues: [...facet.facetValues],
    };

    return html`
      <mhecomm-create-search-country-filter
        id="create-country"
        .defaultCountryCode="${DEFAULT_COUNTRY_CODE}"
        .facet="${countryFacet}">
      </mhecomm-create-search-country-filter>
    `;
  }

  /**
   * Renders the state and school facet together.
   * The state will be fetched from the facets parameter.
   *
   * @param facet
   */
  private renderStateAndSchoolFacet(facet: SearchFacet): TemplateResult | typeof nothing {
    // shallow cloning facets value
    const facets = this.facets?.length
      ? this.facets.map((currentFacet) => ({
          ...currentFacet,
          facetValues: [...currentFacet.facetValues],
        }))
      : this.facets;
    const schoolFacet = {
      ...facet,
      facetValues: [...facet.facetValues],
    };

    return html`
      <mhecomm-create-state-school-search-filter .facets="${facets}" .schoolFacet="${schoolFacet}">
      </mhecomm-create-state-school-search-filter>
    `;
  }

  private renderCheckbox(
    facet: SearchFacet,
    value: SearchFacetValue,
    collapseClass = '',
    shouldPerformCreateSearch = true
  ): TemplateResult {
    return html`
      <div class="flex-row pb-2 ${collapseClass}">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            .checked=${value.selected}
            value=${value.label}
            data-automation-id="${this.replaceSpaceWithinStr(value.name)}"
            id="${this.replaceSpaceWithinStr(value.name)}"
            @change=${() => {
              this.selectFacet(
                facet,
                value,
                value.selected ? FilterAction.REMOVE : FilterAction.ADD,
                false,
                shouldPerformCreateSearch
              );
            }} />
          <label
            class="form-check-label d-flex justify-content-between"
            for="${this.replaceSpaceWithinStr(value.name)}">
            <span> ${value.label} </span> <span>(${value.count})</span>
          </label>
        </div>
      </div>
    `;
  }

  private renderDefaultFacet(facet: SearchFacet): TemplateResult {
    return html`
      <fieldset>
        <legend class="visually-hidden">${facet.label} checkbox</legend>
        ${repeat(
          facet.facetValues,
          (facetValue) => facetValue.name,
          (facetValue, facetIndex) => {
            let collapseClass = '';
            if (facetIndex >= this.displayFacetValueLimit) {
              collapseClass = `collapse-facet-values-${facet.name} collapse`;
            }
            return this.renderCheckbox(facet, facetValue, collapseClass);
          }
        )}
      </fieldset>
      ${facet.facetValues.length > this.displayFacetValueLimit
        ? html` <button
            type="button"
            class="btn btn-link p-0 m-0 align-baseline text-decoration-none"
            aria-haspopup="dialog"
            id="view-all-facet-values-link-${facet.name}"
            @click=${(event) =>
              this.handleFacetValuesCollapseToggle(
                facet,
                facet.facetValues,
                event,
                `view-all-facet-values-link-${facet.name}`
              )}>
            View All
            ${this.isFilterRenderedInModal
              ? html`<uxt-icon
                  class="px-2"
                  id="view-all-facet-values-icon-${facet.name}"
                  icon="chevron-down"
                ></uxt-icon>`
              : nothing}
          </button>`
        : nothing}
    `;
  }

  private handleFacetValuesCollapseToggle(
    facet: SearchFacet,
    facetValues: SearchFacetValue[],
    event: any,
    triggerId: string
  ): void {
    if (event && (event.type === 'click' || (event.type === 'keyup' && event.code === 'Space'))) {
      if (this.isFilterRenderedInModal) {
        const viewAllLink = findElementInContexts<HTMLButtonElement>(
          `#${triggerId}`,
          [this.shadowRoot, event.target.getRootNode(), document]
        ) as HTMLButtonElement;

        const dropDownIcon = findElementInContexts<HTMLElement>(
          `#view-all-facet-values-icon-${facet.name}`,
          [this.shadowRoot, event.target.getRootNode(), document]
        ) as HTMLElement;

        const collapseItems = findElementInContexts<HTMLElement>(
          `.collapse-facet-values-${facet.name}`,
          [this.shadowRoot, event.target.getRootNode(), document],
          true
        ) as NodeListOf<HTMLElement>;

        const collapseClass = `flex-row pb-2 collapse-facet-values-${facet.name} collapse`;

        if (viewAllLink.getAttribute('aria-expanded') === 'true') {
          viewAllLink!.firstChild!.textContent = 'View All';
          viewAllLink.setAttribute('aria-expanded', 'false');
          dropDownIcon?.setAttribute('icon', 'chevron-down');
          collapseItems?.forEach((element) => element.setAttribute('class', collapseClass));
        } else {
          viewAllLink!.firstChild!.textContent = 'View Less';
          viewAllLink.setAttribute('aria-expanded', 'true');
          dropDownIcon?.setAttribute('icon', 'chevron-up');
          collapseItems?.forEach((element) => element.setAttribute('class', `${collapseClass} show`));
        }
      } else {
        const modal: Modal = new Modal({
          header: facet.label,
          isHeaderTextCapitalize: true,
          body: { template: this.getAllFacetValuesTemplate(facet, facetValues) },
          primaryButton: { label: 'Apply' },
          modalContext: { componentRef: `view-all-facet-values` },
        });
        modal.addEventListener('mhecomm-modal-confirmed', () => {
          store.dispatch(performCreateSearch());
          modal.close();
        });
        modal.addEventListener(`view-all-facet-values-modal-close`, () => {
          (this.shadowRoot?.querySelector(`#${triggerId}`) as HTMLInputElement)?.focus();
        });
        modal.open();
      }
    }
  }

  private getAllFacetValuesTemplate(facet: SearchFacet, facetValues: SearchFacetValue[]): TemplateResult {
    return html`
      <style>
        #all-facet-values {
          max-height: 60vh;
        }
      </style>
      <div id="all-facet-values" class="overflow-auto ps-1">
        ${facetValues?.map((facetValue) => this.renderCheckbox(facet, facetValue, '', false))}
      </div>
    `;
  }

  private renderAppliedFilter(): TemplateResult {
    return html`
      <div class="d-flex justify-content-between w-100">
        <h2 class="h6 fw-bold mb-3">Applied Filters</h2>
      </div>
      ${this.appliedFilter?.map(
        (facet) => html` <div class="mb-3">
          <span class="h6 fw-normal">${facet.label}:</span>
          <ul class="list-unstyled">
            ${facet.facetValues?.map(
              (facetValue) => html`
                  <li>
                    <div
                      class="alert alert-secondary alert-dismissible mb-2 py-2 ps-2 d-flex align-items-top"
                    >
                      <div>
                        <p class="mb-0">${facetValue.label}</p>
                        <button
                          data-automation-id="${this.replaceSpaceWithinStr(facetValue.name)}_clear" 
                          id="${this.replaceSpaceWithinStr(facetValue.name)}_clear"
                          type="button"
                          class="btn-close btn-sm pb-2"
                          data-bs-dismiss="alert"
                          aria-label="Remove ${facetValue.label} ${facet.label} Filter"
                          @click=${() => {
                            this.selectFacet(facet, facetValue, FilterAction.REMOVE, true);
                          }}
                        ></button>
                      </div>
                    </div>
                    </li>
                  </div>
                `
            )}
          </ul>
        </div>`
      )}
      ${this.selectedFacetCount
        ? html`
            <button
              class="btn btn-link p-0 mb-3"
              @click=${this.clearAllFilters.bind(this)}
              aria-label="Clear All Filters"
              id="clear_filters"
              data-automation-id="clear_filters">
              Clear Filters
            </button>
          `
        : nothing}
    `;
  }

  private handleCollapseToggle(e: PointerEvent, name: string): void {
    const element = e.target as HTMLElement;
    const collapseId = element.getAttribute('data-bs-target') as string;

    if (!collapseId) {
      return;
    }

    let collapse = this.shadowRoot?.querySelector(collapseId) as HTMLElement;
    let searchContext: Document | ShadowRoot | null = this.shadowRoot;

    if (!collapse) {
      const rootNode = element.getRootNode();
      if (rootNode instanceof Document || rootNode instanceof ShadowRoot) {
        collapse = rootNode.querySelector(collapseId) as HTMLElement;
        searchContext = rootNode;
      }
    }

    if (!collapse) {
      return;
    }

    const collapseInstance = this.bsInit(
      collapseId,
      window.bootstrap?.Collapse,
      { toggle: false },
      searchContext
    )?.instance;

    if (!collapseInstance) {
      return;
    }

    if (element.classList.contains('collapsed')) {
      collapse.addEventListener('show.bs.collapse', () => element.classList.remove('collapsed'), { once: true });
      collapseInstance.show();
      this.expandAnalytics(element, name, true);
      element.setAttribute('aria-expanded', 'true');
    } else {
      collapse.addEventListener('hide.bs.collapse', () => element.classList.add('collapsed'), { once: true });
      collapseInstance.hide();
      this.expandAnalytics(element, name, false);
      element.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Emits analytics events whenever the collapsible filters are expanded or closed.
   *
   * @param element
   * @param name
   * @param opened
   */
  private expandAnalytics(element: any, name: string, opened: boolean): void {
    CreateSearchAnalytics.filterGroup({
      element,
      id: this.id,
      name,
      componentName: 'mhecomm-create-search-filters',
      opened,
      storeCategory: this.store,
    });
  }

  private openModal(): void {
    this.filterModal.open();
    emit({
      element: this,
      name: 'filter-open-modal',
      detail: this.analyticDetail(),
    });
  }

  private udpateFilterPlacement(): void {
    const windowWidth = (): number => {
      const clientWidth = window.document.documentElement.clientWidth;
      const body = window.document.body;
      return (
        (window.document.compatMode === 'CSS1Compat' && clientWidth) ||
        (body && body.clientWidth) ||
        clientWidth
      );
    };

    const current = this.isFilterRenderedInModal;

    if (windowWidth() >= 768) {
      this.isFilterRenderedInModal = false;
    } else {
      this.isFilterRenderedInModal = true;
    }

    if (current !== this.isFilterRenderedInModal) {
      this.requestUpdate();
    }
  }

  private analyticDetail(obj = {}) {
    const state = store.getState() as StoreState;
    return {
      id: this.id,
      store: this.store,
      user: getUserDataForUserAnalytics(state),
      searchPageType: selectSearchPageType(state),
      catalog: this.selectedCatalog,
      ...obj,
    };
  }

  private checkIfDefaultCollapsedFilter(): boolean {
    return this.isDefaultCollapsedFilter && this.collapseFilter;
  }

  private clearAllFilters(): void {
    store
      .dispatch(
        performCreateSearch({
          fieldValueFilter: {},
          searchValue: createSearchParam(store.getState() as StoreState)!,
          start: 1,
          store: this.store,
        })
      )
      .then((): void => {
        CreateSearchAnalytics.filterClearAll({
          element: this,
          id: this.id,
          name: 'filter-clear-all',
          storeCategory: this.store,
        });

        announceFilterChange(FilterAction.CLEAR_ALL, undefined, undefined, this.getAnnouncer());
      });
  }

  private replaceSpaceWithinStr(val: string): string {
    return val.replace(/\s/g, '_');
  }

  private onFilterSelection(event: Event): void {
    const path = event.composedPath();
    const isFromComponent = path.includes(this);
    const isFromComponentModal = this.filterModal ? path.includes(this.filterModal) : false;
    const isFromActiveFilterModal = this.getActiveFilterModal() ? path.includes(this.getActiveFilterModal() as Modal) : false;

    if (!isFromComponent && !isFromComponentModal && !isFromActiveFilterModal) {
      return;
    }

    const detail = (event as CustomEvent).detail;
    this.selectFacet(detail?.facet, detail?.value, detail?.action, detail?.viaPill);
  }

  /**
   * Pre-selects the country facet value based on the provided list of countries and the default country code.
   */
  private preSelectCountry(): void {
    if (this.countries) {
      this.countriesAsFacet = getCountriesAsFacet(this.countries);
      const defaultCountryFacetValue = this.countriesAsFacet.facetValues.find(
        (facetValue) => facetValue.name === DEFAULT_COUNTRY_CODE
      );

      if (defaultCountryFacetValue) {
        this.selectFacet(this.countriesAsFacet, defaultCountryFacetValue, FilterAction.ADD);
      }
    }
  }

  /**
   * Handles facet selection and dispatches the appropriate actions to update the search results and analytics.
   *
   * @param facet
   * @param value
   * @param action
   * @param viaPill
   * @param shouldPerformCreateSearch
   */
  private selectFacet(
    facet: SearchFacet,
    value?: SearchFacetValue,
    action?: FilterAction,
    viaPill?: boolean,
    shouldPerformCreateSearch = true
  ): void {
    if (facet && value && action) {
      updateFacet(facet.name, value, action, this.store, shouldPerformCreateSearch).then((): void => {
        const appliedFilters = this.appliedFilter.map((filter) => ({
          group: filter.name,
          label: filter.label,
        }));

        CreateSearchAnalytics.filterBy({
          appliedFilters: appliedFilters,
          element: this,
          facet: facet,
          id: this.id,
          searchFacetValue: value,
          storeCategory: this.store,
          viaPill: viaPill,
        });

        announceFilterChange(action, facet, value, this.getAnnouncer());
      });
    }
  }

  private getAnnouncer(): HTMLElement | null | undefined {
    return this.shadowRoot?.getElementById('filter-announcements');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-search-filters': CreateSearchFilters;
  }
}
